import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { MuiTelInput as PhoneField } from 'mui-tel-input';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import toast from 'react-hot-toast';

import { useCreateMerchantMutation } from '@/store/api/services/merchants/merchants';
import { useGetPlatformsQuery } from '@/store/api/services/platforms/platforms';
import { useGetMerchantEnumsQuery } from '@/store/api/services/resources/enums';

import { isValidPhone } from '@/forms';

import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

const CreateMerchantSchema = z
  .object({
    company_name: z.string().min(2, 'Company Name is Required'),
    owner_first_name: z.string().min(2, 'First Name is Required'),
    owner_last_name: z.string().min(2, 'Last Name is Required'),
    owner_email: z.string().email('Valid Email is Required'),
    owner_phone_number: isValidPhone(z.string(), 'Valid Phone Number is Required'),
    type: z.enum(['native', 'teller']),
    platform_key: z.string().trim(),
    platform_merchant_id: z.string().trim(),
    notes: z.string().optional(),
    is_serviceable: z.boolean().optional(),
    classification: z.enum(['single_store', 'corporate']).optional().or(z.literal('')),
  })
  .superRefine((data, ctx) => {
    if (data.type === 'teller') {
      if (!data.platform_key) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Platform is required for teller merchants',
          path: ['platform_key'],
        });
      }
      if (!data.platform_merchant_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Platform Merchant ID is required for teller merchants',
          path: ['platform_merchant_id'],
        });
      }
    }
  });

type CreateMerchantSchemaType = z.infer<typeof CreateMerchantSchema>;

const CreateMerchant = ({ hide }: ModalChildrenProps) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CreateMerchantSchemaType>({
    resolver: zodResolver(CreateMerchantSchema),
    mode: 'onSubmit',
    defaultValues: {
      is_serviceable: false,
      classification: 'single_store',
      type: 'native',
      platform_key: '',
      platform_merchant_id: '',
    },
  });

  const type = watch('type');
  const [createMerchant] = useCreateMerchantMutation();
  const { data: merchantEnumsData } = useGetMerchantEnumsQuery();
  const { data: platformsData } = useGetPlatformsQuery();

  const onSubmit = async (payload: CreateMerchantSchemaType) => {
    try {
      const apiPayload: Record<string, any> = { ...payload };
      if (payload.type === 'teller') {
        if (payload.platform_key) apiPayload.platform_key = payload.platform_key;
        if (payload.platform_merchant_id)
          apiPayload.platform_merchant_id = payload.platform_merchant_id;
      } else {
        apiPayload.platform_key = undefined;
        apiPayload.platform_merchant_id = undefined;
      }
      await createMerchant(apiPayload).unwrap();
      toast.success('Merchant created successfully');
      hide('merchant-create');
    } catch (error: any) {
      if (error?.message) {
        const err: { message: string } = error;
        toast.error(err.message);
      }
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 3 }}>
        <Stack gap={2}>
          <TextField
            label="Company Name *"
            fullWidth
            error={!!errors.company_name}
            helperText={errors?.company_name?.message}
            size="small"
            {...register('company_name')}
          />
          <Stack direction="row" gap={2}>
            <TextField
              sx={{ flex: 1 }}
              label="First Name *"
              fullWidth
              error={!!errors.owner_first_name}
              helperText={errors?.owner_first_name?.message}
              size="small"
              {...register('owner_first_name')}
            />
            <TextField
              sx={{ flex: 1 }}
              label="Last Name *"
              fullWidth
              error={!!errors.owner_last_name}
              helperText={errors?.owner_last_name?.message}
              size="small"
              {...register('owner_last_name')}
            />
          </Stack>
          <Stack direction="row" gap={2}>
            <TextField
              label="Email *"
              fullWidth
              error={!!errors.owner_email}
              helperText={errors?.owner_email?.message}
              size="small"
              {...register('owner_email')}
            />
            <Controller
              name="owner_phone_number"
              control={control}
              defaultValue=""
              render={({ field: { value, onChange } }) => (
                <PhoneField
                  fullWidth
                  defaultCountry="US"
                  label="Phone Number *"
                  size="small"
                  value={value}
                  onChange={(_, info) => onChange(info.numberValue)}
                  error={!!errors.owner_phone_number}
                  helperText={errors?.owner_phone_number?.message}
                />
              )}
            />
          </Stack>
          <Controller
            name="is_serviceable"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value || false}
                    onChange={field.onChange}
                    color="primary"
                  />
                }
                label="Is Serviceable"
              />
            )}
          />
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!errors.type}>
                <InputLabel>Type</InputLabel>
                <Select
                  {...field}
                  label="Type"
                  value={field.value || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                  }}
                >
                  <MenuItem value="native">Native</MenuItem>
                  <MenuItem value="teller">Teller</MenuItem>
                </Select>
                {errors.type && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.type.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          {type === 'teller' && (
            <>
              <Controller
                name="platform_key"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.platform_key}>
                    <InputLabel>Platform</InputLabel>
                    <Select
                      {...field}
                      label="Platform"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {platformsData?.data?.map((platform) => (
                        <MenuItem key={platform.id} value={platform.key}>
                          {platform.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.platform_key && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {errors.platform_key.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
              <TextField
                error={!!errors.platform_merchant_id}
                helperText={errors?.platform_merchant_id?.message}
                label="Platform Merchant ID"
                fullWidth
                size="small"
                {...register('platform_merchant_id')}
              />
            </>
          )}
          <Controller
            name="classification"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!errors.classification}>
                <InputLabel>Classification</InputLabel>
                <Select
                  {...field}
                  label="Classification"
                  value={field.value || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Normalize the value to ensure it matches the schema
                    let normalizedValue = value.toLowerCase().replace(/\s+/g, '_');
                    // Handle case where API returns "singlestore" -> convert to "single_store"
                    if (normalizedValue === 'singlestore') {
                      normalizedValue = 'single_store';
                    }
                    field.onChange(value === '' ? undefined : normalizedValue);
                  }}
                >
                  {merchantEnumsData?.data?.classifications?.map((classification) => {
                    // Normalize enum value to lowercase with underscores
                    let normalizedValue = classification.toLowerCase().replace(/\s+/g, '_');
                    // Handle case where API returns "singlestore" -> convert to "single_store"
                    if (normalizedValue === 'singlestore') {
                      normalizedValue = 'single_store';
                    }
                    // Create display label by splitting on underscores and capitalizing each word
                    const displayLabel = normalizedValue
                      .split('_')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(' ');
                    return (
                      <MenuItem key={classification} value={normalizedValue}>
                        {displayLabel}
                      </MenuItem>
                    );
                  })}
                </Select>
                {errors.classification && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.classification.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Notes
            </Typography>
            <TextareaAutosize
              {...register('notes')}
              minRows={4}
              maxRows={8}
              placeholder="Enter notes about this merchant..."
              style={{
                width: '100%',
                resize: 'vertical',
                padding: '12px 14px',
                border: errors.notes ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
              }}
            />
            {errors.notes && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.notes.message}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={() => hide('merchant-create')}>Discard</Button>
        <LoadingButton variant="contained" loading={isSubmitting} onClick={handleSubmit(onSubmit)}>
          Create Merchant
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default CreateMerchant;
